import nextRoutes from 'next-routes';
import React, { forwardRef } from 'react';
import { omit, pickBy } from 'lodash';

// This singleton design is used to provide a convenient API for the consumer.
// Applications can call appPage() to initialize, then import and use these
// utility components and functions anywhere within client-side code, and the
// Next.js and next-routes routers will "just work".
//
// This uninitialized, default next-routes is used as a placeholder to prevent
// the below utility functions and components from throwing errors in unit and
// integration tests. This prevents the need to call appPage() to
// initialize this library in tests.
let nextRoutesSingleton = nextRoutes();

export const initializeNextRoutes = routes => {
  nextRoutesSingleton = nextRoutes();

  if (routes) {
    routes.forEach(route => nextRoutesSingleton.add(route));
  }
};

// DEPRECATED
// This function exists only for legacy compatibility with the web-ssr Link component, to
// which it is tightly coupled. Use the Link component in your application instead.
//
// Returns a boolean indicating if the specified URL represents a valid route
// that the Next.js/next-routes application is responsible for rendering.
export const isMatchingRoute = url => {
  return Boolean(nextRoutesSingleton.match(url).route);
};

// An imperative API for nagivating to a new URL. Similar to clicking on an <Link>.
export const pushRoute = url => {
  nextRoutesSingleton.Router.pushRoute(url);
};

export const Link = forwardRef(function (props, ref) {
  try {
    // If we are building a link that is local and can be client-side navigated
    // then we build a Next.js compatible <Link>, otherwise return a regular link.
    const isHrefAbsolute = /^(https?:)?\/\//.test(props.href);
    const matchResult =
      !isHrefAbsolute && props.href && nextRoutesSingleton.match(props.href);

    if (matchResult && matchResult.route) {
      const allowedProps = props && pickBy(omit(props, ['href']));

      const NextRoutesLink = nextRoutesSingleton.Link;

      return (
        <NextRoutesLink
          params={matchResult.params}
          passHref={true}
          prefetch={false}
          route={matchResult.route.name}
        >
          {/* eslint-disable jsx-a11y/anchor-is-valid */}
          <a
            {...allowedProps}
            data-next-name={matchResult.route.name}
            ref={ref}
          >
            {props.children}
          </a>
        </NextRoutesLink>
      );
    }

    return <a {...props}>{props.children}</a>;
  } catch (e) {
    // Return a vanilla link if we encounter an error parsing the provided
    // `href`. At worst this causes a page refresh to the next page but protets
    // against errors occuring.
    return <a {...props}>{props.children}</a>;
  }
});
